/**
 * M.E.A. Email Categories
 *
 * API functions for Marta email categories (virtual folders)
 * Uses AI GraphQL endpoint (api-server) for M.E.A. operations
 */

import { aiGraphqlQuery, aiGraphqlMutation } from '../../../database/graphqlClient.js';

// =============================================================================
// TYPES
// =============================================================================

export interface EmailCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
  classification: string;
  unreadCount: number;
  totalCount: number;
  isSystem: boolean;
  sortOrder: number;
}

export interface EmailPreview {
  id: string;
  uid: number;
  folder: string;
  subject: string;
  fromName?: string;
  fromAddress: string;
  receivedAt: string;
  isImportant: boolean;
}

export interface EmailCategorySummary {
  category: EmailCategory;
  previewEmails: EmailPreview[];
}

// =============================================================================
// API FUNCTIONS (AI GraphQL Endpoint - api-server)
// =============================================================================

/**
 * Get all email categories with counts
 */
export async function getEmailCategories(): Promise<EmailCategory[]> {
  try {
    const data = await aiGraphqlQuery<{ emailCategories: EmailCategory[] }>(`
      query {
        emailCategories {
          id name icon color classification
          unreadCount totalCount isSystem sortOrder
        }
      }
    `);
    return data.emailCategories || [];
  } catch (error) {
    console.error('[M.E.A.] Failed to get categories:', error);
    return [];
  }
}

/**
 * Get important emails for Today/Hub view
 */
export async function getImportantEmails(limit: number = 10): Promise<EmailPreview[]> {
  try {
    const data = await aiGraphqlQuery<{ importantEmails: EmailPreview[] }>(`
      query ImportantEmails($limit: Int) {
        importantEmails(limit: $limit) {
          id uid folder subject fromName fromAddress receivedAt isImportant
        }
      }
    `, { limit });
    return data.importantEmails || [];
  } catch (error) {
    console.error('[M.E.A.] Failed to get important emails:', error);
    return [];
  }
}

/**
 * Get category summaries for badges (unread counts + previews)
 */
export async function getEmailCategorySummaries(): Promise<EmailCategorySummary[]> {
  try {
    const data = await aiGraphqlQuery<{ emailCategorySummaries: EmailCategorySummary[] }>(`
      query {
        emailCategorySummaries {
          category {
            id name icon color classification unreadCount totalCount
          }
          previewEmails {
            id uid folder subject fromName fromAddress receivedAt isImportant
          }
        }
      }
    `);
    return data.emailCategorySummaries || [];
  } catch (error) {
    console.error('[M.E.A.] Failed to get category summaries:', error);
    return [];
  }
}

/**
 * Get emails in a specific category
 */
export async function getEmailsInCategory(
  categoryId: string,
  limit: number = 50,
  offset: number = 0
): Promise<EmailPreview[]> {
  try {
    const data = await aiGraphqlQuery<{ emailsInCategory: EmailPreview[] }>(`
      query EmailsInCategory($categoryId: ID!, $limit: Int, $offset: Int) {
        emailsInCategory(categoryId: $categoryId, limit: $limit, offset: $offset) {
          id uid folder subject fromName fromAddress receivedAt isImportant
        }
      }
    `, { categoryId, limit, offset });
    return data.emailsInCategory || [];
  } catch (error) {
    console.error('[M.E.A.] Failed to get emails in category:', error);
    return [];
  }
}

/**
 * Create a custom category
 */
export async function createEmailCategory(
  name: string,
  icon?: string,
  color?: string
): Promise<EmailCategory | null> {
  try {
    const data = await aiGraphqlMutation<{ createEmailCategory: EmailCategory }>(`
      mutation CreateCategory($input: CreateCategoryInput!) {
        createEmailCategory(input: $input) {
          id name icon color classification unreadCount totalCount isSystem sortOrder
        }
      }
    `, { input: { name, icon, color } });
    return data.createEmailCategory || null;
  } catch (error) {
    console.error('[M.E.A.] Failed to create category:', error);
    return null;
  }
}

/**
 * Delete a custom category
 */
export async function deleteEmailCategory(categoryId: string): Promise<boolean> {
  try {
    const data = await aiGraphqlMutation<{ deleteEmailCategory: boolean }>(`
      mutation DeleteCategory($categoryId: ID!) {
        deleteEmailCategory(categoryId: $categoryId)
      }
    `, { categoryId });
    return data.deleteEmailCategory === true;
  } catch (error) {
    console.error('[M.E.A.] Failed to delete category:', error);
    return false;
  }
}

/**
 * Move email to category (triggers M.E.A. learning)
 */
export async function moveEmailToCategory(
  emailUid: number,
  fromFolder: string,
  toCategoryId: string
): Promise<{ success: boolean; learningApplied: boolean; error?: string }> {
  try {
    const data = await aiGraphqlMutation<{
      moveEmailToCategory: { success: boolean; learningApplied: boolean; error?: string };
    }>(`
      mutation MoveToCategory($emailUid: Int!, $fromFolder: String!, $toCategoryId: ID!) {
        moveEmailToCategory(emailUid: $emailUid, fromFolder: $fromFolder, toCategoryId: $toCategoryId) {
          success
          learningApplied
          error
        }
      }
    `, { emailUid, fromFolder, toCategoryId });
    const result = data.moveEmailToCategory;
    return {
      success: result?.success || false,
      learningApplied: result?.learningApplied || false,
      error: result?.error,
    };
  } catch (error) {
    console.error('[M.E.A.] Failed to move email to category:', error);
    return {
      success: false,
      learningApplied: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Batch classify unread emails in INBOX
 */
export async function classifyInboxEmails(limit: number = 20): Promise<number> {
  try {
    const data = await aiGraphqlMutation<{ classifyInboxEmails: number }>(`
      mutation ClassifyInbox($limit: Int) {
        classifyInboxEmails(limit: $limit)
      }
    `, { limit });
    return data.classifyInboxEmails || 0;
  } catch (error) {
    console.error('[M.E.A.] Failed to classify inbox:', error);
    return 0;
  }
}

// =============================================================================
// CLASSIFICATION REVIEW & CORRECTION
// =============================================================================

/**
 * Classified email with full details for review
 */
export interface ClassifiedEmail {
  id: string;
  uid: number;
  folder: string;
  subject: string;
  fromName?: string;
  fromAddress: string;
  receivedAt: string;
  classification: string;
  categoryName: string;
  categoryIcon: string;
  isRead: boolean;
}

/**
 * Get recent classifications for review/correction
 */
export async function getRecentClassifications(limit: number = 10): Promise<ClassifiedEmail[]> {
  try {
    const data = await aiGraphqlQuery<{ recentClassifications: ClassifiedEmail[] }>(`
      query RecentClassifications($limit: Int) {
        recentClassifications(limit: $limit) {
          id uid folder subject fromName fromAddress receivedAt
          classification categoryName categoryIcon isRead
        }
      }
    `, { limit });
    return data.recentClassifications || [];
  } catch (error) {
    console.error('[M.E.A.] Failed to get recent classifications:', error);
    return [];
  }
}

/**
 * Input for classification correction
 */
export interface CorrectClassificationInput {
  emailId: string;
  originalClassification: string;
  correctedClassification: string;
  emailSubject?: string;
  emailFrom?: string;
  emailBody?: string;
  correctionNote?: string;
}

/**
 * Submit a classification correction
 * The correction will be saved for admin review and training
 */
export async function correctClassification(
  input: CorrectClassificationInput
): Promise<{ success: boolean; error?: string }> {
  try {
    const data = await aiGraphqlMutation<{
      correctClassification: { success: boolean; error?: string };
    }>(`
      mutation CorrectClassification($input: CorrectClassificationInput!) {
        correctClassification(input: $input) {
          success
          error
        }
      }
    `, { input });
    return data.correctClassification || { success: false, error: 'No response' };
  } catch (error) {
    console.error('[M.E.A.] Failed to submit correction:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get classification label in English
 */
export function getClassificationLabel(classification: string): string {
  const labels: Record<string, string> = {
    work_important: 'Important',
    work_routine: 'Routine',
    advertisement: 'Ads',
    spam: 'Spam',
  };
  return labels[classification] || classification;
}

/**
 * Get category by classification
 */
export function getCategoryByClassification(
  categories: EmailCategory[],
  classification: string
): EmailCategory | undefined {
  return categories.find(c => c.classification === classification);
}

/**
 * Calculate total unread count across all categories
 */
export function getTotalUnreadCount(categories: EmailCategory[]): number {
  return categories.reduce((sum, cat) => sum + cat.unreadCount, 0);
}

/**
 * Format category badge text
 */
export function formatCategoryBadge(category: EmailCategory): string {
  if (category.unreadCount === 0) return '';
  return `${category.icon} ${category.unreadCount} ${category.name}`;
}
